// Morfi-aware palette builders. Bridges `@/config/outcome-colors` (the
// canonical 16-hue × 11-step outcome system) into the pattern-studio lib so
// consumers can render an outcome-aligned dither/generative pattern by hue
// name alone.
//
// At render time the renderer only knows hex strings — these helpers
// resolve the relevant CSS variables (mode-resolved oklch values) into hex
// via the generic token resolver. Re-resolve after a theme toggle if you
// want the new mode's values.

import {
  OUTCOME_HUES,
  type OutcomeHueName,
  type OutcomePaletteStep,
} from '@/config/outcome-colors';
import { resolvePaletteFromTokens } from '../core/browser/token-resolver';
import type { Palette } from '../types';

export { OUTCOME_HUES, type OutcomeHueName, type OutcomePaletteStep };

/**
 * Default 6-stop ramp for outcome palettes — light → dark gradient that
 * gives dither / generative patterns a usable brightness range without
 * pinning at the 50/950 extremes (which collapse to near-white / near-black
 * in many palettes and make dimmer cells invisible).
 *
 * Index 0 is the dimmest stop and is the natural background match per the
 * pattern lib's auto-bg-sync convention (see `pattern-studio-section.tsx`).
 */
export const DEFAULT_OUTCOME_STEPS: readonly OutcomePaletteStep[] = [900, 700, 500, 300, 200, 100];

/**
 * Build the array of CSS custom-property names that compose an outcome
 * ramp. Pure function — no DOM access — so it's safe at module load.
 *
 * Maps to `--outcome-{hue}-{step}` (the bare primitive ramp, NOT the
 * `--color-outcome-*` semantic tokens). See `outcomeHueToStepVar` in
 * `@/config/outcome-colors` for the canonical bridge.
 */
export function outcomeRampTokens(
  hue: OutcomeHueName,
  steps: readonly OutcomePaletteStep[] = DEFAULT_OUTCOME_STEPS,
): string[] {
  return steps.map((step) => `--outcome-${hue}-${String(step)}`);
}

/**
 * Resolve an outcome ramp into a hex `Palette` ready to feed into a
 * `PatternState`. Returns an empty array in SSR / when the document isn't
 * available — caller should guard.
 */
export function resolveOutcomePalette(
  hue: OutcomeHueName,
  steps?: readonly OutcomePaletteStep[],
): Palette {
  return resolvePaletteFromTokens(outcomeRampTokens(hue, steps));
}
